/* eslint-disable import/no-cycle */
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { twMerge } from 'tailwind-merge';
import { IMeeting } from 'types/meetings';

import { subscribeBattlecards } from 'actions/websockets';

import useTrackRevIntelEvents from 'hooks/tracking/useRevIntelEvents';
import useFeature from 'hooks/useFeature';

import { useGetMeetingMethodologyQuery } from 'services/meetings';

import AvomaHoverCard from 'components/Common/AvomaHoverCard';
import AvomaLink from 'components/Common/AvomaLink';
import AvomaTooltipV2 from 'components/Common/AvomaTooltipV2';
import ButtonUnstyled from 'components/Common/ButtonUnstyled';
import NotificationBanner from 'components/Common/NotificationBanner';
import Spinner from 'components/Common/Spinner';

import L10n from 'helpers/L10n';
import { L10nMeetings } from 'helpers/L10nMeetings';
import Licenses from 'helpers/Licenses';
import BotState from 'helpers/botState';
import { formatSecondsToTimestamp } from 'helpers/date';
import { isZenMode } from 'helpers/desktopApp';
import { getLiveMethodologyMentorStatus } from 'helpers/getLiveMethodologyMentorStatus';
import { L10nRevenueIntelligence } from 'helpers/l10n/L10RevenueIntelligence';
import MeetingState from 'helpers/meetingState';
import {
  isCloudMeetingNotAutoRecording,
  isMeetingHappening,
  isMeetingPast
} from 'helpers/meetingsData';

import { ReactComponent as GreenCheckIcon } from 'images/green_check.svg';
import { ReactComponent as ExpandIcon } from 'images/ic_expand.svg';
import { ReactComponent as InfoIcon } from 'images/ic_info.svg';

interface IMethodologyMentorProps {
  meeting: IMeeting;
}

interface IMethodologyCustomCategoryMapping {
  order: number;
  customCategory: IMethodologyCustomCategory;
}

interface IMethodologyCustomCategory {
  key: string;
  uuid: string;
  customCategorySymbol: string;
  name: string;
  label?: string;
  description?: string;
}

interface IMethodology {
  data: {
    playbook?: {
      uuid: string;
      playbookLabel: string;
    };
    customCategoryMapping?: IMethodologyCustomCategoryMapping[];
    rtAssistantData?: any;
  };
  isLoading: boolean;
}

const MethodologyMentor: FC<IMethodologyMentorProps> = ({ meeting }) => {
  const dispatch = useDispatch();

  const hasDealIntelligenceFeature = useFeature(
    Licenses.FEATURES.DealIntelligence
  );

  const { trackAiMethodologyEvent } = useTrackRevIntelEvents();

  const { data: methodologyData, isLoading: isMethodologyLoading } =
    useGetMeetingMethodologyQuery<IMethodology>(
      {
        meetingUuid: meeting?.uuid
      },
      {
        skip:
          !meeting?.uuid ||
          isMeetingPast(meeting) ||
          !hasDealIntelligenceFeature
      }
    );

  const realtimeTranscriptions = useSelector(
    (state: RootState) => state.realtimeTranscriptions?.data
  );

  const realtimeAssistant = useSelector(
    (state: RootState) => state.realtimeAssistant.methodology
  );

  const rtData = useMemo(() => {
    const fromApi = methodologyData?.rtAssistantData;

    return Array.isArray(fromApi) && fromApi.length === 0
      ? realtimeAssistant
      : fromApi ?? realtimeAssistant;
  }, [realtimeAssistant, methodologyData]);

  const mappedItems = useMemo(() => {
    const keysFromPlaybook = methodologyData?.customCategoryMapping?.map(
      (item: IMethodologyCustomCategoryMapping) => item.customCategory.key
    );

    return (rtData || [])
      .filter(
        (item: { playbookCategoryMapping: { customCategoryKey: string } }) =>
          keysFromPlaybook?.includes(
            item.playbookCategoryMapping?.customCategoryKey
          )
      )
      .map(
        (item: {
          playbookCategoryMapping: { customCategoryKey: string };
          covered: boolean;
          timestamp: number;
        }) => ({
          ...item,
          key: item.playbookCategoryMapping?.customCategoryKey,
          covered: item.covered,
          timestamp: item.timestamp
        })
      );
  }, [methodologyData?.customCategoryMapping, rtData]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isMeetingEndingSoon, setIsMeetingEndingSoon] = useState(() =>
    moment(meeting.endAt).isBefore(moment().add(5, 'minutes'))
  );
  const [isMeetingInMiddlePhase, setIsMeetingInMiddlePhase] = useState(() => {
    const now = moment();
    const startTime = moment(meeting.startAt);
    const endTime = moment(meeting.endAt);
    const totalDuration = endTime.diff(startTime, 'minutes');
    const elapsedTime = now.diff(startTime, 'minutes');

    // Calculate percentage of meeting completed
    const percentageComplete = (elapsedTime / totalDuration) * 100;

    // Check if meeting is ending soon (within 5 minutes)
    const isEndingSoon = endTime.isBefore(now.add(5, 'minutes'));

    // Return true if meeting is 40%+ complete AND not ending soon
    return percentageComplete >= 40 && !isEndingSoon;
  });
  const [contentHeight, setContentHeight] = useState(0);
  const [animatingTopics, setAnimatingTopics] = useState<Set<string>>(
    new Set()
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const previousTopicStatesRef = useRef<Record<string, boolean>>({});
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (meeting) {
      dispatch(subscribeBattlecards(meeting.uuid));
    }
  }, [dispatch, meeting]);

  // Track when methodology mentor is viewed (when methodology data is loaded)
  useEffect(() => {
    if (methodologyData && !isMethodologyLoading) {
      trackAiMethodologyEvent({
        action: 'VIEWED',
        props: {
          methodology_id: methodologyData?.playbook?.uuid ?? null,
          methodology_name: methodologyData?.playbook?.playbookLabel ?? null
        },
        view: 'AI_METHODOLOGY_MENTOR'
      });
    }
  }, [methodologyData, isMethodologyLoading, trackAiMethodologyEvent]);

  useEffect(() => {
    // Check every 30 seconds for in-progress meetings
    const interval = setInterval(() => {
      const wasEndingSoon = isMeetingEndingSoon;
      const wasInMiddlePhase = isMeetingInMiddlePhase;

      const endingSoon = moment(meeting.endAt).isBefore(
        moment().add(5, 'minutes')
      );

      setIsMeetingEndingSoon(endingSoon);

      // Also check for middle phase
      const now = moment();
      const startTime = moment(meeting.startAt);
      const endTime = moment(meeting.endAt);
      const totalDuration = endTime.diff(startTime, 'minutes');
      const elapsedTime = now.diff(startTime, 'minutes');

      const percentageComplete = (elapsedTime / totalDuration) * 100;
      const inMiddlePhase = percentageComplete >= 40 && !endingSoon;

      setIsMeetingInMiddlePhase(inMiddlePhase);

      // Track phase changes
      if (!wasEndingSoon && endingSoon) {
        trackAiMethodologyEvent({
          action: 'MEETING_ENDING_SOON_DETECTED',
          props: {
            methodology_id: methodologyData?.playbook?.uuid ?? null,
            methodology_name: methodologyData?.playbook?.playbookLabel ?? null
          },
          view: 'AI_METHODOLOGY_MENTOR'
        });
      }

      if (!wasInMiddlePhase && inMiddlePhase) {
        trackAiMethodologyEvent({
          action: 'MEETING_MIDDLE_PHASE_DETECTED',
          props: {
            methodology_id: methodologyData?.playbook?.uuid ?? null,
            methodology_name: methodologyData?.playbook?.playbookLabel ?? null
          },
          view: 'AI_METHODOLOGY_MENTOR'
        });
      }
    }, 30000); // 30 seconds

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [
    meeting?.startAt,
    meeting?.endAt,
    isMeetingEndingSoon,
    isMeetingInMiddlePhase,
    trackAiMethodologyEvent,
    methodologyData?.playbook?.uuid,
    methodologyData?.playbook?.playbookLabel
  ]);

  useEffect(() => {
    if (mappedItems && mappedItems.length > 0) {
      const currentStates: Record<string, boolean> = {};
      const newlyAnimatingTopics = new Set<string>();
      const previousStates = previousTopicStatesRef.current;

      mappedItems.forEach((topic: { covered: boolean; key: string }) => {
        const isCurrentlyCovered = topic.covered;
        const wasPreviouslyCovered = previousStates[topic.key];

        currentStates[topic.key] = isCurrentlyCovered;

        // If topic just became covered, add it to animating set and track the event
        if (isCurrentlyCovered && !wasPreviouslyCovered) {
          newlyAnimatingTopics.add(topic.key);

          // Find the topic details for tracking
          const topicDetails = methodologyData?.customCategoryMapping?.find(
            (item: IMethodologyCustomCategoryMapping) =>
              item.customCategory.key === topic.key
          );

          if (topicDetails) {
            trackAiMethodologyEvent({
              action: 'TOPIC_COVERED',
              props: {
                methodology_id: methodologyData?.playbook?.uuid ?? null,
                methodology_name:
                  methodologyData?.playbook?.playbookLabel ?? null,
                topic_name: topicDetails.customCategory.name
              },
              view: 'AI_METHODOLOGY_MENTOR'
            });
          }
        }
      });

      // Update the ref with current states
      previousTopicStatesRef.current = currentStates;

      if (newlyAnimatingTopics.size > 0) {
        setAnimatingTopics(newlyAnimatingTopics);

        // Clear any existing timeout to prevent memory leaks
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }

        // Clear animations after animation duration
        animationTimeoutRef.current = setTimeout(() => {
          setAnimatingTopics(new Set());
          animationTimeoutRef.current = null;
        }, 2000); // 2 second animation duration
      }
    }

    // Cleanup function to clear timeout on unmount or dependency change
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [
    mappedItems,
    methodologyData,
    methodologyData?.playbook?.uuid,
    methodologyData?.playbook?.playbookLabel,
    trackAiMethodologyEvent,
    isExpanded
  ]);

  useEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      setContentHeight(height);
    }
  }, [isExpanded, methodologyData, rtData]);

  const toggleExpanded = useCallback(() => {
    const willBeExpanded = !isExpanded;
    setIsExpanded(willBeExpanded);

    trackAiMethodologyEvent({
      action: willBeExpanded ? 'EXPANDED' : 'COLLAPSED',
      props: {
        methodology_id: methodologyData?.playbook?.uuid ?? null,
        methodology_name: methodologyData?.playbook?.playbookLabel ?? null
      },
      view: 'AI_METHODOLOGY_MENTOR'
    });
  }, [
    isExpanded,
    trackAiMethodologyEvent,
    methodologyData?.playbook?.uuid,
    methodologyData?.playbook?.playbookLabel
  ]);

  // Debounced handler for topic hover events using Lodash
  const handleTopicHover = useMemo(
    () =>
      debounce((topicName: string) => {
        trackAiMethodologyEvent({
          action: 'TOPIC_HOVERED',
          props: {
            methodology_id: methodologyData?.playbook?.uuid ?? null,
            methodology_name: methodologyData?.playbook?.playbookLabel ?? null,
            topic_name: topicName
          },
          view: 'AI_METHODOLOGY_MENTOR'
        });
      }, 500),
    [
      trackAiMethodologyEvent,
      methodologyData?.playbook?.uuid,
      methodologyData?.playbook?.playbookLabel
    ]
  );

  // Cleanup debounced function on unmount
  useEffect(
    () => () => {
      handleTopicHover.cancel();
    },
    [handleTopicHover]
  );

  const isLiveStreamingEnabled = useMemo(() => {
    if (isEmpty(meeting)) {
      return false;
    }

    let retVal = false;

    const noAutoRecording = isCloudMeetingNotAutoRecording(meeting);

    if (!isEmpty(realtimeTranscriptions)) {
      retVal = true;
    }

    if (meeting.botState === BotState.dialedIn) {
      retVal = true;
    }

    const meetingStateKey = meeting.states?.meeting?.key;

    if (
      meetingStateKey === MeetingState.botRecStart ||
      meetingStateKey === MeetingState.botRecPause ||
      meetingStateKey === MeetingState.botRecResume
    ) {
      retVal = true;
    }

    if (
      meeting.botState === BotState.recordingUsingIntegration &&
      !noAutoRecording &&
      (meetingStateKey === MeetingState.zoomRecStart ||
        meetingStateKey === MeetingState.msTeamsRecordingStart)
    ) {
      retVal = true;
    }

    return retVal;
  }, [meeting, realtimeTranscriptions]);

  const getTopicIndicatorStyles = useMemo(
    () => (isTopicCovered: boolean, topicKey?: string) => {
      const baseStyles =
        'relative text-xs inline-flex items-center justify-center w-8 h-8 rounded border cursor-pointer flex-shrink-0 transition-all duration-300';

      const isAnimating = topicKey && animatingTopics.has(topicKey);

      if (isTopicCovered) {
        return twMerge(
          baseStyles,
          'bg-green-lighter text-green-dark border-green-light/50',
          isAnimating && 'animate-scaleX shadow-lg shadow-green-light/30'
        );
      }

      if (isMeetingEndingSoon) {
        return twMerge(
          baseStyles,
          'bg-avoma-red/10 border-avoma-red/20 text-avoma-red outline outline-avoma-red/20 animate-pulse'
        );
      }

      if (isMeetingInMiddlePhase) {
        return twMerge(
          baseStyles,
          'bg-yellow-lighter text-yellow-darker border-yellow-light/50'
        );
      }

      return twMerge(baseStyles, 'bg-f1 border-gainsboro text-silver');
    },
    [isMeetingEndingSoon, isMeetingInMiddlePhase, animatingTopics]
  );

  const renderLiveStreamingDisabledBanner = () => {
    const { conferenceData } = meeting;

    const service = conferenceData?.service?.toLowerCase();
    const recordByInt = conferenceData?.recordByInt;

    const isZoom = service === 'zoom';
    const isCloudRecordingService = ['msteam', 'googlemeet', 'webex'].includes(
      service ?? ''
    );
    const hasNoSupportedCloudRecordingConnection =
      isCloudRecordingService && !recordByInt;

    const { showLiveStreamingWarning, showCloudRecordingError } =
      getLiveMethodologyMentorStatus({
        hasZoomConnection: isZoom,
        hasSupportedCloudRecordingConnection:
          !hasNoSupportedCloudRecordingConnection,
        isLiveStreamingEnabledForUser: isLiveStreamingEnabled,
        // this part of code will only be reached if DeskBot is not enabled
        isAvBotSelected: false
      });

    return (
      <React.Fragment>
        {showLiveStreamingWarning && (
          <NotificationBanner
            type='warning'
            className='m-4'
            content={
              L10nRevenueIntelligence.aiMethodology.liveMethodologyMentor
                .liveStreamingWarning
            }
            sm
            additionalAction={
              <AvomaLink
                to={L10n.zoom.allowLiveStreamingCheckboxHelpDoc}
                className='shrink-0 font-normal text-sm leading-5 tracking-[var(--app-letter-spacing-default)] underline text-blue'
                openNewWindow
              >
                {
                  L10nRevenueIntelligence.aiMethodology.liveMethodologyMentor
                    .viewRecordingPreference
                }
              </AvomaLink>
            }
          />
        )}

        {showCloudRecordingError && (
          <NotificationBanner
            type='error'
            content={
              L10nRevenueIntelligence.aiMethodology.liveMethodologyMentor
                .cloudRecordingNotSupportedError
            }
            sm
          />
        )}
      </React.Fragment>
    );
  };

  if (!methodologyData || !isMeetingHappening(meeting)) {
    return null;
  }

  return (
    <div
      data-testid='methodology-mentor'
      data-meeting-ending-soon={isMeetingEndingSoon}
      data-meeting-middle-phase={isMeetingInMiddlePhase}
      data-expanded={isExpanded}
      role='region'
      aria-label={L10nMeetings.methodologyMentorTitle}
      className='relative flex flex-col -mb-4 px-3.5 py-2.5 gap-2 text-gunmetal border-b border-gainsboro'
    >
      <div className='flex flex-wrap items-center justify-between gap-x-2'>
        <span className='text-base font-bold'>
          {L10nMeetings.methodologyMentorTitle}
        </span>

        <AvomaHoverCard
          triggerComponent={
            <div
              data-testid='methodology-mentor-info-icon'
              className='z-[2] flex items-center justify-center w-6 h-6 cursor-pointer rounded transition-all ease-in-out bg-snow hover:bg-f1'
            >
              <InfoIcon className='w-4 h-4 flex-shrink-0' />
            </div>
          }
          childrenClasses='flex flex-col overflow-y-auto !p-0'
          contentClasses={twMerge('z-[999] min-w-[330px]')}
          contentProps={{
            align: 'end',
            sideOffset: 8
          }}
        >
          <div className='flex flex-col gap-y-2 p-2 text-sm'>
            {isLiveStreamingEnabled && (
              <p>
                Avoma AI is analyzing this conversation in real time to keep you
                on track with the{' '}
                <strong>{methodologyData?.playbook?.playbookLabel}</strong>{' '}
                methodology.
              </p>
            )}

            {!isZenMode() && (
              <p>
                For the optimal screen sharing experience, use{' '}
                <strong>zen mode</strong> in our{' '}
                <AvomaLink
                  to='https://www.avoma.com/download'
                  className='font-bold'
                  openNewWindow
                >
                  desktop app
                </AvomaLink>
                . Monitor methodology adherence seamlessly—without Avoma
                appearing in your shared view.
              </p>
            )}
          </div>
        </AvomaHoverCard>
      </div>

      {/* Loading state */}
      {isMethodologyLoading && (
        <div className='flex flex-col items-center justify-center gap-x-2 py-2'>
          <Spinner type='small' />
          <span className='text-sm text-silver-dark'>
            {L10nMeetings.methodologyMentorLoading}
          </span>
        </div>
      )}

      {!isLiveStreamingEnabled && renderLiveStreamingDisabledBanner()}

      {/* Collapsed view - show icons in a row */}
      {!isExpanded &&
        methodologyData?.customCategoryMapping &&
        methodologyData.customCategoryMapping.length > 0 && (
          <div className='flex flex-wrap items-center gap-4 mt-1.5'>
            {methodologyData.customCategoryMapping.map(
              (item: IMethodologyCustomCategoryMapping) => {
                const topicObj = mappedItems.find(
                  (topic: { key: string }) =>
                    topic.key === item.customCategory.key
                );

                const isTopicCovered = topicObj?.covered;
                const isAnimating =
                  topicObj?.key && animatingTopics.has(topicObj.key);

                return (
                  <AvomaHoverCard
                    key={item.customCategory.uuid}
                    triggerComponent={
                      <div
                        className={getTopicIndicatorStyles(
                          !!isTopicCovered,
                          item.customCategory.key
                        )}
                        title={item.customCategory.name}
                        data-topic-key={item.customCategory.key}
                        data-topic-covered={!!isTopicCovered}
                        data-topic-animating={!!isAnimating}
                        role='checkbox'
                        tabIndex={0}
                        aria-checked={!!isTopicCovered}
                        aria-busy={!!isAnimating}
                        aria-label={`${item.customCategory.name} - ${
                          isTopicCovered ? 'Covered' : 'Not covered'
                        }`}
                        onMouseEnter={() =>
                          handleTopicHover(item.customCategory.name)
                        }
                      >
                        {isAnimating ? (
                          <GreenCheckIcon className='w-4 h-4 flex-shrink-0' />
                        ) : (
                          item.customCategory.customCategorySymbol
                        )}
                      </div>
                    }
                    childrenClasses='flex flex-col overflow-y-auto !p-0'
                    contentClasses={twMerge('z-[999] min-w-[400px]')}
                    contentProps={{
                      align: 'start',
                      sideOffset: 8
                    }}
                  >
                    <div
                      className={twMerge('p-3', isTopicCovered && 'opacity-50')}
                    >
                      <label
                        htmlFor={`checklist-item-${item.customCategory.uuid}`}
                        className='flex flex-1 flex-col leading-relaxed text-silver-dark'
                      >
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-sm'>
                            {item.customCategory.name}
                          </span>
                        </div>

                        <span className='text-gunmetal'>
                          {item.customCategory?.label ??
                            item.customCategory?.description}
                        </span>
                      </label>
                    </div>
                  </AvomaHoverCard>
                );
              }
            )}
          </div>
        )}

      {/* Expanded view - full content */}
      <div
        className={twMerge(
          'flex flex-col w-full transition-[max-height,opacity] duration-300 ease-in-out overflow-hidden mb-1',
          isExpanded ? 'opacity-100' : 'opacity-0'
        )}
        style={{
          maxHeight: isExpanded ? `${contentHeight}px` : '0px'
        }}
      >
        <div ref={contentRef} className='overflow-hidden'>
          <div className='box-border flex flex-col space-y-3 px-1'>
            {methodologyData?.customCategoryMapping?.map(
              (item: IMethodologyCustomCategoryMapping) => {
                const topicObj = mappedItems.find(
                  (topic: { key: string }) =>
                    topic.key === item.customCategory.key
                );

                const isTopicCovered = topicObj?.covered;
                const isAnimating =
                  topicObj?.key && animatingTopics.has(topicObj.key);

                return (
                  <div
                    key={item.customCategory.uuid}
                    className={twMerge('group flex items-center gap-4')}
                  >
                    <div
                      className={getTopicIndicatorStyles(
                        !!isTopicCovered,
                        item.customCategory.key
                      )}
                      data-topic-key={item.customCategory.key}
                      data-topic-covered={!!isTopicCovered}
                      data-topic-animating={!!isAnimating}
                      role='checkbox'
                      tabIndex={0}
                      aria-checked={!!isTopicCovered}
                      aria-busy={!!isAnimating}
                      aria-label={`${item.customCategory.name} - ${
                        isTopicCovered ? 'Covered' : 'Not covered'
                      }`}
                      onMouseEnter={() =>
                        handleTopicHover(item.customCategory.name)
                      }
                    >
                      {isAnimating ? (
                        <GreenCheckIcon className='w-4 h-4 flex-shrink-0' />
                      ) : (
                        item.customCategory.customCategorySymbol
                      )}
                    </div>

                    <div
                      className={twMerge(
                        'flex flex-col gap-y-2 w-full min-w-0',
                        isTopicCovered && 'opacity-50'
                      )}
                    >
                      <label
                        htmlFor={`checklist-item-${item.customCategory.uuid}`}
                        className='flex flex-1 flex-col leading-relaxed text-silver-dark'
                      >
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-xs truncate'>
                            {item.customCategory.name}
                          </span>
                          {topicObj?.timestamp && (
                            <span className='text-xs text-silver-dark underline'>
                              {formatSecondsToTimestamp(topicObj.timestamp)}
                            </span>
                          )}
                        </div>

                        <span className='text-gunmetal text-sm'>
                          {item.customCategory?.label ??
                            item.customCategory?.description}
                        </span>
                      </label>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>

      {isLiveStreamingEnabled && (
        <div className='z-10 absolute -bottom-3.5 left-1/2 transform -translate-x-1/2 flex items-center justify-center cursor-pointer'>
          <AvomaTooltipV2
            tooltip={
              isExpanded
                ? L10nMeetings.methodologyCardCollapse
                : L10nMeetings.methodologyCardExpand
            }
            tooltipPosition='bottom-center'
          >
            <ButtonUnstyled
              data-testid='methodology-mentor-expand-collapse-button'
              className='flex items-center justify-center cursor-pointer bg-white rounded-full p-1.5 border border-gainsboro shadow-sm hover:shadow-md hover:bg-f1 transition-all duration-200'
              onClick={toggleExpanded}
              noDefaultStyles
            >
              <ExpandIcon
                className={twMerge(
                  'w-3 h-3 transition-transform duration-300 ease-in-out',
                  !isExpanded && 'rotate-180'
                )}
              />
            </ButtonUnstyled>
          </AvomaTooltipV2>
        </div>
      )}
    </div>
  );
};

export default MethodologyMentor;
